import { useNavigate } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import PageLoader from '@/components/PageLoader';
import SubPageHero from '@/components/SubPageHero';
import Button from '@/components/ui/Button';
import { TRUST } from '@/data/mockData';
import c from './Company.module.scss';     // 회사소개 라이트 섹션 재사용
import x from './CompanyCertifications.module.scss';
import { GUIDE_ICON } from '@/components/GuideIcons';
const Ic = ({ n }: { n: string }) => <div className={x.cardIcon}>{GUIDE_ICON[n]}</div>; // [2026-10-08] 라이트 카드 라인아이콘(네이비+시안)

// ============================================================================
//  회사소개 — 인증 · 기술  (/company/certifications)
//  [2026-10-08] 기획서 v7 §8(인증·기술) + §4-2 인증·연구소. 라이트 테마(회사소개 체계).
//    실데이터: ②인증·협력 기관 로고(TRUST.partners 6기관, evstech 실측)
//    준비중(공개용 증서·번호·유효기간 대기): ③KC·환경공단 증서 ④연구소 인정서·특허·상표 증빙
//    [2026-10-26 사용자] 제품 일반 소구 '한국환경공단 인증' → '승인·확인'으로 전사 통일(evstech 대조: G200은 '인증'이 아닌 승인·확인 성격).
//      단 아래 CERTS의 'IoT 통신 프로토콜 3.0/복수/교대 인증'은 evstech 주요연혁 실측과 동일한 공식 이벤트명이라 그대로 유지(실제 프로토콜 인증 이력). 상세: docs/EVS_v7_전수비교_2026-10-26.md
// ============================================================================

const Reveal = ({ className = '', children }: { className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};

function Soon({ label }: { label: string }) {
  return (
    <div className={x.soon} role="img" aria-label={`${label} 준비중`}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      <span>{label} · 준비중</span>
    </div>
  );
}

// [2026-10-11 사용자] 공식 브로셔(251128 pg8) + newsData.ts:696(ENV_CERTS) 이중 일치로 공식 인증 명칭·발급월 확정.
//   → 기존 '공개 자료 확보 후 게시' placeholder(증서원문·번호 대기)는 '증서원문/번호' 용도이므로 보존하고,
//     확정된 '인증 명칭·발급월'만 기존 라이트 카드 언어에 맞춰 리스트로 '가산'. (근거: [[evs-brochure-251128-data]])
//   ISO 45001(안전보건경영시스템)=안전 정체성 근거. 날짜 표기는 브로셔 그대로(23.9/24.6/24.8).
const CERTS: { t: string; d: string }[] = [
  { t: '한국환경공단 IoT 통신 프로토콜 3.0 인증', d: '2023.09' },
  { t: "한국환경공단 IoT 통신 프로토콜 '복수' 인증", d: '2024.06' },
  { t: '한국환경공단 2024년 2/4분기 전송률 우수 GW 제조사 선정', d: '2024.08' },
  { t: "한국환경공단 IoT 통신 프로토콜 '교대' 인증", d: '2024.08' },
  { t: 'ISO 45001 안전보건경영시스템 인증', d: '보유' },
];

export default function CompanyCertifications() {
  const navigate = useNavigate();
  const nav = (to: string) => navigate(to);

  return (
    <main>
      <PageLoader />
      <SubPageHero
        align="left"
        title="Certifications"
        subtitle="검증된 기술력과 공인 인증"
        image="/images/company/subpage-hero.webp"
        crumbs={[{ label: 'HOME', to: '/' }, { label: '회사소개', to: '/company' }, { label: '인증·기술' }]}
      />

      {/* ① 개요 */}
      <section className={c.section}>
        <div className={c.inner}>
          <Reveal>
            <p className={c.eyebrow}>TECHNOLOGY &amp; CERTIFICATION</p>
            <h2 className={c.headline}>검증된 기술력과 공인 인증</h2>
            <p className={c.lead}>{TRUST.tagline}</p>
            <p className={c.body}>이브이에스는 게이트웨이 자체 제조·개발 역량과 기업부설연구소를 바탕으로, 공인 기관의 인증·승인을 받은 IoT 대기환경·산업안전 솔루션을 공급합니다.</p>
          </Reveal>
        </div>
      </section>

      {/* ② 인증·협력 기관 */}
      <section className={`${c.section} ${c.alt}`}>
        <div className={c.inner}>
          <Reveal>
            <p className={c.eyebrow}>PARTNERS</p>
            <h2 className={c.headline}>{TRUST.title}</h2>
            <div className={x.logoGrid}>
              {TRUST.partners.map((p) => (
                <div key={p.name} className={x.logoCell}><img src={p.src} alt={p.name} loading="lazy" /></div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ③ KC · 환경공단 승인 */}
      <section className={c.section}>
        <div className={c.inner}>
          <Reveal>
            <p className={c.eyebrow}>CERTIFICATION</p>
            <h2 className={c.headline}>KC · 환경공단 승인</h2>
            <div className={x.cardGrid}>
              <div className={x.card}>
                <Ic n="certified" /><span className={x.badge}>KC</span>
                <h3>게이트웨이 KC</h3>
                <p>IoT 게이트웨이(EVS-G200) 단수형·복수형·교대형 전 모델에 대한 KC 인증 정보를 게시합니다. 공식 인증명칭·번호·발급일·대상 모델과 증서 원문을 제공합니다.</p>
              </div>
              <div className={x.card}>
                <Ic n="doc" /><span className={x.badge}>K-ECO</span>
                <h3>한국환경공단 승인·확인</h3>
                <p>대기배출 IoT 측정기기의 환경공단 관련 승인·확인 문서와 적용 전송 프로토콜(GreenLink)을 안내합니다. 공식 명칭은 원문 기준으로 표기합니다.</p>
              </div>
            </div>
            <ul className={x.certList}>
              {CERTS.map((ce) => (
                <li key={ce.t} className={x.certItem}>
                  <span className={x.certIco}>{GUIDE_ICON.certified}</span>
                  <span className={x.certName}>{ce.t}</span>
                  <span className={x.certDate}>{ce.d}</span>
                </li>
              ))}
            </ul>
            <Soon label="공개용 증서 원문·인증번호·유효기간" />
            <p className={x.note}>※ 위 인증은 공식 자료(회사 브로셔) 기준입니다. 증서 원문(PDF)·인증번호는 순차 게시합니다.</p>
          </Reveal>
        </div>
      </section>

      {/* ④ 연구소 · 벤처 · 특허·상표 */}
      <section className={`${c.section} ${c.alt}`}>
        <div className={c.inner}>
          <Reveal>
            <p className={c.eyebrow}>R&amp;D · IP</p>
            <h2 className={c.headline}>연구소 · 벤처 · 특허</h2>
            <div className={`${x.cardGrid} ${x.three}`}>
              <div className={x.card}><Ic n="solution" /><h3>기업부설연구소</h3><p>자체 기업부설연구소에서 하드웨어·펌웨어·응용 소프트웨어를 개발하고 제품을 개선합니다.</p></div>
              <div className={x.card}><Ic n="energy" /><h3>벤처기업</h3><p>기술 기반 벤처기업으로서 IoT 계측·관제 기술을 지속 고도화합니다.</p></div>
              <div className={x.card}><Ic n="certified" /><h3>특허 · 상표</h3><p>독자 기술과 브랜드를 특허·상표로 보호하고 있습니다.</p></div>
            </div>
            <Soon label="연구소 인정서·특허·상표 증빙" />
          </Reveal>
        </div>
      </section>

      {/* ⑤ 행동 */}
      <section className={c.section}>
        <div className={c.inner}>
          <Reveal>
            <p className={c.eyebrow}>CONTACT</p>
            <h2 className={c.headline}>자세한 자료가 필요하신가요?</h2>
            <p className={c.lead}>인증서·사양서·회사 소개 자료를 안내해 드립니다.</p>
            <div className={x.ctaRow}>
              <Button variant="pill" onClick={() => nav('/support/inquiry')}>문의하기</Button>
              <a className={x.ghost} onClick={() => nav('/support/resources')}>자료실 보기</a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
